"use client";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Link from "next/link";

export type EmptyStateAction = {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: LucideIcon;
  variant?: "default" | "outline" | "ghost" | "secondary";
};

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: EmptyStateAction;
  /** Optional second CTA (e.g. clear filters + browse all). */
  secondaryAction?: EmptyStateAction;
  className?: string;
  compact?: boolean;
  /** Framed panel — used in Creator Studio empty states */
  variant?: "default" | "framed";
}

function variantWrapClass(variant: "default" | "framed" | undefined, compact: boolean) {
  if (variant === "framed") {
    return cn(
      "w-full rounded-2xl border border-border/50 bg-card/20 px-4 sm:px-8 py-10 sm:py-12 shadow-sm shadow-black/5",
      compact && "py-8"
    );
  }
  return undefined;
}

function ActionButton({ action, compact }: { action: EmptyStateAction; compact: boolean }) {
  const v = action.variant ?? "default";
  return action.href ? (
    <Button size={compact ? "sm" : "default"} variant={v} asChild>
      <Link href={action.href} className="flex items-center gap-1.5">
        {action.icon && <action.icon className="h-4 w-4" />}
        {action.label}
      </Link>
    </Button>
  ) : (
    <Button size={compact ? "sm" : "default"} variant={v} onClick={action.onClick} className="flex items-center gap-1.5">
      {action.icon && <action.icon className="h-4 w-4" />}
      {action.label}
    </Button>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  compact = false,
  variant = "default",
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        variant === "default" && (compact ? "py-8 gap-3" : "py-14 gap-4"),
        variantWrapClass(variant, compact),
        variant === "framed" && "gap-4",
        className
      )}
    >
      {/* Icon with gradient blur bg */}
      <div className="relative">
        <div
          className={cn(
            "absolute inset-0 rounded-full blur-xl scale-150",
            variant === "framed"
              ? "bg-primary/15 ring-1 ring-primary/10"
              : "bg-primary/10"
          )}
        />
        <div
          className={cn(
            "relative rounded-full bg-muted/60 border border-border/50 flex items-center justify-center",
            compact ? "h-12 w-12" : "h-16 w-16"
          )}
        >
          <Icon className={cn("text-muted-foreground", compact ? "h-5 w-5" : "h-7 w-7")} />
        </div>
      </div>

      {/* Text */}
      <div className={cn("max-w-xs", compact ? "space-y-1" : "space-y-1.5")}>
        <h3 className={cn("font-display font-semibold", compact ? "text-base" : "text-lg")}>{title}</h3>
        <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>{description}</p>
      </div>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className={cn("flex flex-col sm:flex-row items-center justify-center gap-2", compact && "gap-1.5")}>
          {action && <ActionButton action={action} compact={compact} />}
          {secondaryAction && <ActionButton action={secondaryAction} compact={compact} />}
        </div>
      )}
    </div>
  );
}
